import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { CloseEventArgs, DialogComponent } from '@syncfusion/ej2-angular-popups';
import { catchError, finalize, Observable, Subject, Subscription, tap } from 'rxjs';
import { PessoaDoencaInsercaoDto } from 'src/app/dtos/doencas/pessoa-doenca-dto';
import { SelectDataSource } from 'src/app/libs/ax-select/select-data-source';
import { ToastService } from 'src/app/libs/toast/toast.service';
import { Cid10Service } from 'src/app/servicos/cid10.service';
import { PessoaDoencaService } from 'src/app/servicos/pessoa-doenca.service';
import { AtendimentoAtualConfiguracao } from '../../atendimento-medico/atendimento-cadastro/atendimento-cadastro.component';
import { TranslateService } from '@ngx-translate/core';
import { ALERTA, DOENCA } from 'src/app/i18n/constantes';
import { ConfirmService } from 'src/app/libs/confirm/confirm.service';
import { AxSelectComponent } from 'src/app/libs/ax-select/ax-select.component';
import { ThisReceiver } from '@angular/compiler';

interface VincularDoencaForm {
  pessoaId: FormControl<number>,
  cid10Codigo: FormControl<string>,
  dataAtendimento: FormControl<Date>,
  atendimentoMedicoId: FormControl<number | null>,
  descricao: FormControl<string | null>
}

@Component({
  selector: 'app-doenca-insercao',
  templateUrl: './doenca-insercao.component.html',
  styleUrls: ['./doenca-insercao.component.less']
})
export class DoencaInsercaoComponent implements OnInit, OnDestroy {
  modalOk: boolean = false;
  
  _atendimentoAtualConfiguracao: AtendimentoAtualConfiguracao | null = null;
  
  @ViewChild('cidSelect')
  cidSelect!: AxSelectComponent;

  @ViewChild('modal')
  modal!: DialogComponent;

  submetido: boolean = false;
  emOperacao: boolean = false;
  temAlteracao: boolean = false;

  subsModelo: Subscription | null = null;
  
  modelo: FormGroup<VincularDoencaForm> = this.fb.group<VincularDoencaForm>({
    pessoaId: this.fb.nonNullable.control<number>(0, Validators.required),
    cid10Codigo: this.fb.nonNullable.control<string>('', Validators.required),
    dataAtendimento: this.fb.nonNullable.control<Date>(new Date(), Validators.required),
    descricao: this.fb.nonNullable.control<string | null>(null),
    atendimentoMedicoId: this.fb.nonNullable.control<number | null>(null)
  });

  dsCids10: SelectDataSource = this.cid10Servico.dsCids10();

  assunto: Subject<boolean> = new Subject<boolean>();

  _atendimento: { pessoaId: number | null ; data: Date | null  } | null = null;

  @Input()
  set atendimentoAtualConfiguracao(atendimento: AtendimentoAtualConfiguracao | null) {
    this._atendimentoAtualConfiguracao = atendimento;

    if(atendimento === null) {
      this.modelo.reset({}, { emitEvent: false });
      return;
    }

    this.modelo.controls.pessoaId.setValue(atendimento.pessoaId ?? 0, { emitEvent: false });
    this.modelo.controls.atendimentoMedicoId.setValue(atendimento.id ?? null, { emitEvent: false });
    this.modelo.controls.cid10Codigo.setValue('', { emitEvent: false });
    this.modelo.controls.dataAtendimento.setValue(atendimento.data ?? new Date(), { emitEvent: false });
  }

  drt: boolean = false;

  constructor(private fb: FormBuilder, 
    private cid10Servico: Cid10Service, 
    private toast: ToastService,
    private confirm: ConfirmService,
    private translate: TranslateService,
    private pessoaDoencaServico: PessoaDoencaService) { }

  ngOnInit(): void {
    this.subsModelo = this.modelo.valueChanges.subscribe(() =>  {
      this.temAlteracao = true;

      if((this.cidSelect.val as any)?.drt === true) {
        this.drt = true;
      }
    });
  }

  ngOnDestroy(): void {
    this.subsModelo?.unsubscribe();
  }

  vincular() {
    this.submetido = true;

    if(this.emOperacao)
      return;

    if(!this.modelo.valid)
      return;

    let doenca: PessoaDoencaInsercaoDto = {
      pessoaId: this.modelo.controls.pessoaId.value,
      cid10Id: this.modelo.controls.cid10Codigo.value,
      dataAtendimento: this.modelo.controls.dataAtendimento.value,
      dataCura: null,
      atendimentoMedicoId: this.modelo.controls.atendimentoMedicoId.value,
      descricao: this.modelo.controls.descricao.value,
    };

    this.emOperacao = true;
    this.pessoaDoencaServico.inserirDoenca(doenca)
    .pipe(
      catchError(err => {
        this.toast.showWarning(this.translate.instant(DOENCA.ERRO.VINCULACAO));
        throw err;
      }),
      finalize(() => {
        this.emOperacao = false;
      })
    )
    .subscribe(() => {
      this.temAlteracao = false;
      this.emOperacao = false;
      this.toast.showSuccess(this.translate.instant(DOENCA.SUCESSO.VINCULACAO));
      this.modal.hide();
    });
    
  }

  fechandoModal(args: CloseEventArgs) {
    if(this.emOperacao) {
      args.cancel = true;
      return;
    }

    if(this.temAlteracao) {
      args.cancel = true;
      this.confirmarFechamento();
      return;
    }

    this.assunto.next(true);
  }

  abrir(): Observable<boolean> {
    this.submetido = false;
    
    this.drt = false;

    this.modal.show(true);
    this.modelo.controls.cid10Codigo.setValue('', { emitEvent: false });
    this.modelo.controls.descricao.setValue('', { emitEvent: false });
    
    return new Observable((subscriber) => {
      let subs = this.assunto.subscribe((val) => {
        subs.unsubscribe();
        subscriber.next(val);
        subscriber.complete();
      });

      return { unsubscribe: () => subs.unsubscribe() }
    })
  }

  confirmarFechamento() {
    this.confirm
    .confirm(this.translate.instant(ALERTA.OPERACAO_PENDENTE), { height: '220px' })
    .subscribe(val => {
      if(val) {
        this.cancelar();
      }
    })
  }

  cancelar() {
    this.temAlteracao = false;
    this.modal.hide();
  }
}
function ViewModel(arg0: string): (target: DoencaInsercaoComponent, propertyKey: "cidSelect") => void {
  throw new Error('Function not implemented.');
}

